import type {
  OblivionMineAction,
  OblivionMinePosition,
} from "@cloudnest/oblivion-mine-engine";
import { isPlayerId } from "@cloudnest/oblivion-mine-engine";

/**
 * Typed replay-event adapter for 망각의 지뢰 (oblivion-mine).
 *
 * Hidden-information game: the persisted `init` event carries the full
 * authoritative state (both players' mines); the replay runner masks it for
 * display. Player events are `LayMines` and `Move`. Malformed events fail loudly
 * (unknown type / playerId / position) rather than being silently coerced.
 */
export type OblivionMineReplayEventType = "init" | "LayMines" | "Move";

export const OBLIVION_MINE_REPLAY_EVENT_TYPES = [
  "init",
  "LayMines",
  "Move",
] as const satisfies readonly OblivionMineReplayEventType[];

export function normalizeOblivionMineReplayEventType(
  eventType: string,
): OblivionMineReplayEventType {
  if (
    (OBLIVION_MINE_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)
  ) {
    return eventType as OblivionMineReplayEventType;
  }
  throw new Error(`Unsupported oblivion-mine event type: ${eventType}`);
}

function asPosition(value: unknown): OblivionMinePosition {
  if (
    typeof value === "object" &&
    value !== null &&
    "row" in value &&
    "col" in value
  ) {
    const { row, col } = value as { row: unknown; col: unknown };
    if (typeof row === "number" && typeof col === "number") {
      return { row, col };
    }
  }
  throw new Error("Invalid oblivion-mine position in event payload");
}

function asPlayerId(value: unknown) {
  if (isPlayerId(value)) return value;
  throw new Error(`Invalid oblivion-mine playerId in event payload: ${value}`);
}

/**
 * Convert a persisted relay event (everything except `init`) into a typed
 * engine action. Throws on an unsupported type or malformed payload so a
 * corrupt fixture fails loudly rather than silently dropping/coercing events.
 */
export function oblivionMineEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): OblivionMineAction {
  const type = normalizeOblivionMineReplayEventType(eventType);
  if (type === "init") {
    throw new Error("oblivionMineEventToAction cannot convert the init event");
  }
  const playerId = asPlayerId(payload.playerId);
  if (type === "LayMines") {
    if (!Array.isArray(payload.cells)) {
      throw new Error("Invalid oblivion-mine LayMines cells in event payload");
    }
    return {
      type: "LayMines",
      playerId,
      cells: payload.cells.map(asPosition),
    };
  }
  return { type: "Move", playerId, to: asPosition(payload.to) };
}
